import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
// Composant Terminal sans dépendances externes pour l'affichage de code

// Command definitions come from translation files
const commandExamples = [
  {
    name: 'migrate',
    example: 'dblift migrate --config=dblift.yaml --migrations-dir=./migrations'
  },
  {
    name: 'undo',
    example: 'dblift undo --target-version=1.0.0'
  },
  {
    name: 'validate',
    example: 'dblift validate --migrations-dir=./migrations'
  },
  {
    name: 'info',
    example: 'dblift info'
  },
  {
    name: 'baseline',
    example: 'dblift baseline --baseline-version=1.0.0'
  },
  {
    name: 'repair',
    example: 'dblift repair'
  }
];

const Terminal = () => {
  const { t, i18n } = useTranslation();
  const [currentCommandIndex, setCurrentCommandIndex] = useState(0);
  const [typing, setTyping] = useState('');
  const [showOutput, setShowOutput] = useState(false);

  useEffect(() => {
    // Reset state when command changes
    setTyping('');
    setShowOutput(false);
    
    // Type current command character by character
    const currentCommand = commandExamples[currentCommandIndex].example;
    let i = 0;
    
    const typingInterval = setInterval(() => {
      if (i < currentCommand.length) {
        setTyping(prev => prev + currentCommand.charAt(i));
        i++;
      } else {
        clearInterval(typingInterval);
        // Show output after typing is complete
        setTimeout(() => {
          setShowOutput(true);
          // Switch to next command after a delay
          setTimeout(() => {
            setCurrentCommandIndex((prev) => (prev + 1) % commandExamples.length);
          }, 3000);
        }, 500);
      }
    }, 50);
    
    return () => clearInterval(typingInterval);
  }, [currentCommandIndex]);

  const getCommandOutput = () => {
    const command = commandExamples[currentCommandIndex];
    switch (command.name) {
      case 'migrate':
        return `[INFO] 2023-10-15 14:32:10 - DBLift version 1.5.2
[INFO] 2023-10-15 14:32:11 - Migrating database (Oracle 19c)
[INFO] 2023-10-15 14:32:11 - Checking history table DBLIFT_SCHEMA_HISTORY
[INFO] 2023-10-15 14:32:12 - Found 5 pending migrations
[INFO] 2023-10-15 14:32:12 - Executing migration V1_0_0__Initial_schema.sql
[SUCCESS] 2023-10-15 14:32:14 - Migration successful (27 statements, 235ms)
[INFO] 2023-10-15 14:32:14 - Executing migration V1_0_1__Add_users_table.sql
[SUCCESS] 2023-10-15 14:32:16 - Migration successful (3 statements, 186ms)
[INFO] 2023-10-15 14:32:16 - Executing migration V1_0_2__Add_products_table.sql
[SUCCESS] 2023-10-15 14:32:17 - Migration successful (4 statements, 124ms)
[INFO] 2023-10-15 14:32:17 - Executing migration V1_1_0__Add_foreign_keys.sql
[SUCCESS] 2023-10-15 14:32:18 - Migration successful (5 statements, 142ms)
[INFO] 2023-10-15 14:32:18 - Executing repeatable migration R__Create_view_active_users.sql
[SUCCESS] 2023-10-15 14:32:19 - Migration successful (1 statement, 78ms)
[SUCCESS] 2023-10-15 14:32:19 - Successfully applied 5 migrations (total: 765ms)`;
      case 'undo':
        return `[INFO] 2023-10-15 15:02:05 - DBLift version 1.5.2
[INFO] 2023-10-15 15:02:06 - Connecting to database (Oracle 19c)
[INFO] 2023-10-15 15:02:06 - Reverting to version 1.0.0
[INFO] 2023-10-15 15:02:07 - Found 2 migrations to revert
[INFO] 2023-10-15 15:02:07 - Executing undo script U1_1_0__Remove_foreign_keys.sql
[SUCCESS] 2023-10-15 15:02:08 - Undo successful (5 statements, 157ms)
[INFO] 2023-10-15 15:02:08 - Executing undo script U1_0_2__Drop_products_table.sql
[SUCCESS] 2023-10-15 15:02:09 - Undo successful (1 statement, 86ms)
[INFO] 2023-10-15 15:02:09 - Database schema reverted to version 1.0.1
[SUCCESS] 2023-10-15 15:02:10 - Successfully reverted 2 migrations (total: 243ms)`;
      case 'validate':
        return `[INFO] 2023-10-15 15:34:22 - DBLift version 1.5.2
[INFO] 2023-10-15 15:34:23 - Validating migrations against database (Oracle 19c)
[INFO] 2023-10-15 15:34:23 - Loading migration files from ./migrations
[INFO] 2023-10-15 15:34:24 - Found 7 migration files
[INFO] 2023-10-15 15:34:24 - Validating version order and duplicates... 
[SUCCESS] 2023-10-15 15:34:25 - No duplicate version numbers found
[INFO] 2023-10-15 15:34:25 - Validating SQL syntax in migration scripts...
[SUCCESS] 2023-10-15 15:34:26 - All scripts have valid SQL syntax
[INFO] 2023-10-15 15:34:26 - Validating checksums of applied migrations...
[SUCCESS] 2023-10-15 15:34:26 - All applied migrations match their checksums
[SUCCESS] 2023-10-15 15:34:26 - Validation completed: All migrations are valid!`;
      case 'info':
        return `[INFO] 2023-10-15 16:02:45 - DBLift version 1.5.2
[INFO] 2023-10-15 16:02:46 - Connected to Oracle 19c (19.0.0.0.0)
[INFO] 2023-10-15 16:02:46 - Schema: MYSCHEMA

Current schema version: 1.0.1
Schema history table: DBLIFT_SCHEMA_HISTORY

Applied migrations:
✓ V1_0_0__Initial_schema.sql              | 2023-10-15 14:32:14 | 27 statements | SUCCESS
✓ V1_0_1__Add_users_table.sql             | 2023-10-15 14:32:16 | 3 statements  | SUCCESS
✓ R__Create_view_active_users.sql         | 2023-10-15 14:32:19 | 1 statement   | SUCCESS

Pending migrations:
⬕ V1_0_2__Add_products_table.sql
⬕ V1_1_0__Add_foreign_keys.sql

Migration summary:
- 3 applied migrations
- 2 pending migrations
- 0 failed migrations
- Last successful migration: 2023-10-15 14:32:19`;
      case 'baseline':
        return `[INFO] 2023-10-15 16:28:14 - DBLift version 1.5.2
[INFO] 2023-10-15 16:28:15 - Connecting to database (Oracle 19c)
[INFO] 2023-10-15 16:28:15 - Setting baseline version to 1.0.0
[INFO] 2023-10-15 16:28:16 - Checking for DBLIFT_SCHEMA_HISTORY table
[INFO] 2023-10-15 16:28:16 - Creating history table in schema MYSCHEMA
[INFO] 2023-10-15 16:28:17 - Marking all migrations up to 1.0.0 as applied
[INFO] 2023-10-15 16:28:17 - Creating baseline entry for version 1.0.0
[SUCCESS] 2023-10-15 16:28:18 - Successfully set baseline at version 1.0.0
[INFO] 2023-10-15 16:28:18 - Database is now ready for versioned migrations`;
      case 'repair':
        return `[INFO] 2023-10-15 16:45:32 - DBLift version 1.5.2
[INFO] 2023-10-15 16:45:33 - Connecting to database (Oracle 19c)
[INFO] 2023-10-15 16:45:33 - Starting repair of schema history
[INFO] 2023-10-15 16:45:34 - Checking for inconsistencies
[WARN] 2023-10-15 16:45:34 - Found failed migration: V1_0_2__Add_products_table.sql
[INFO] 2023-10-15 16:45:35 - Removing failed migration from history
[WARN] 2023-10-15 16:45:35 - Checksum mismatch for: R__Create_view_active_users.sql
[INFO] 2023-10-15 16:45:36 - Updating checksum in history table
[INFO] 2023-10-15 16:45:36 - Checking for orphaned migrations
[SUCCESS] 2023-10-15 16:45:37 - Schema history repair completed successfully
[INFO] 2023-10-15 16:45:37 - Run 'dblift validate' to verify the repair was successful`;
      default:
        return 'Command executed successfully';
    }
  };

  return (
    <div className="bg-dblift-blue-dark rounded-lg overflow-hidden shadow-2xl w-full max-w-4xl">
      <div className="bg-dblift-blue-dark px-4 py-3 flex items-center">
        <div className="flex space-x-2">
          <span className="w-3 h-3 rounded-full bg-red-500"></span>
          <span className="w-3 h-3 rounded-full bg-yellow-500"></span>
          <span className="w-3 h-3 rounded-full bg-green-500"></span>
        </div>
        <div className="flex-1 text-center text-white text-sm font-medium">
          {i18n.language.startsWith('fr') ? 'Ligne de commande DBLift' : 'DBLift Command Line'}
        </div>
      </div>
      <div className="p-4 bg-[#282c34] h-[450px] overflow-y-auto font-mono text-xs md:text-sm">
        <div className="text-white">
          <div className="mb-4">
            <span className="text-dblift-orange">$ </span>
            <span className="text-white">{typing}</span>
            {typing.length === commandExamples[currentCommandIndex].example.length ? null : <span className="animate-pulse">|</span>}
          </div>
          {showOutput && (
            <div className="whitespace-pre-wrap">
              {getCommandOutput().split('\n').map((line, index) => {
                if (line.includes('[SUCCESS]')) {
                  return <div key={index} className="text-green-400">{line}</div>;
                } else if (line.includes('[INFO]')) {
                  return <div key={index} className="text-blue-300">{line}</div>;
                } else if (line.includes('[WARN]')) {
                  return <div key={index} className="text-yellow-300">{line}</div>;
                } else if (line.includes('✓')) {
                  return <div key={index} className="text-green-300">{line}</div>;
                } else if (line.includes('⬕')) {
                  return <div key={index} className="text-gray-400">{line}</div>;
                } else {
                  return <div key={index}>{line}</div>;
                }
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Terminal;